import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => localStorage.getItem('token');

const pdsApiClient = axios.create({
  baseURL: config.api.clientFormsServiceUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// All Personal Data Sheet admin routes are namespaced under /admin/pds on
// the unified Client Forms service.
//
// There are deliberately no Tamarac lookups here: a PDS goes to a prospect
// who has not been set up in Tamarac yet, so records are created from a
// plain name and email.
const ADMIN = '/admin/pds';

pdsApiClient.interceptors.request.use(
  (cfg) => {
    const token = getToken();
    if (token) {
      cfg.headers['Authorization'] = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error),
);

applyAuthRetryInterceptor(pdsApiClient);
applyTokenRefreshInterceptor(pdsApiClient);

const pdsService = {
  fetchAllClients: async ({ signal } = {}) => {
    const response = await pdsApiClient.get(`${ADMIN}/clients`, { signal });
    return response.data;
  },

  createClient: async (clientData) => {
    const response = await pdsApiClient.post(`${ADMIN}/clients`, clientData);
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await pdsApiClient.delete(`${ADMIN}/clients/${clientId}`);
    return response.data;
  },

  // Rotates the client's token and emails the reminder copy. The initial
  // invite still goes out through Power Automate; this is Client Services
  // nudging a prospect who has not finished the form since that first email.
  // Note the token rotation invalidates any link already sent.
  resendLink: async (clientId) => {
    const response = await pdsApiClient.post(`${ADMIN}/clients/${clientId}/resend`);
    return response.data;
  },

  // Same endpoint, but `send_notification=false` suppresses the send: this
  // just mints a fresh link for the advisor to paste somewhere themselves.
  getLink: async (clientId) => {
    const response = await pdsApiClient.post(
      `${ADMIN}/clients/${clientId}/resend?send_notification=false`,
    );
    return response.data;
  },

  getClientDetail: async (clientId, { signal } = {}) => {
    const response = await pdsApiClient.get(`${ADMIN}/clients/${clientId}`, { signal });
    return response.data;
  },

  patchClient: async (clientId, fields) => {
    const response = await pdsApiClient.patch(`${ADMIN}/clients/${clientId}`, fields);
    return response.data;
  },
};

export default pdsService;
